import React, { Fragment, type ReactNode } from "react"

import { useTranslation } from "react-i18next"
import { useIsFetching } from "react-query"

import { Grid } from "@mui/material"
import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import { useWorkflowFeatures } from "../../features/Workflows/utils/workflowHelpers"
import { WorkflowDeploymentConstantFeature } from "../../types/generated/api/WorkflowDeploymentConstantFeature"
import { BaseTable } from "./BaseTable"

export function ProjectConstantsTable(): React.ReactElement {
  const { t } = useTranslation("tables")
  const constants = useWorkflowFeatures(new Set(["ConstantFeatures"]))

  const isCurrentProjectFetching = useIsFetching(["project"])

  // columns for project schema table
  const columns: MRT_ColumnDef<WorkflowDeploymentConstantFeature>[] = [
    {
      header: t("projectConstants.columns.constantName"),
      accessorKey: "name",
      Cell: ({ row }) => {
        if (constants.length > 0) {
          return <Typography variant="p">{constants[row.index]?.name}</Typography>
        }
      },
    },
    {
      header: t("projectConstants.columns.type"),
      accessorKey: "type",
      Cell: ({ row }) => {
        if (constants.length > 0) {
          return (
            <Typography style={{ textTransform: "capitalize" }} variant="p">
              {constants[row.index]?.type?.toLowerCase()}
            </Typography>
          )
        }
      },
    },
    {
      header: t("projectConstants.columns.source"),
      accessorKey: "source",
      Cell: ({ row }) => {
        if (constants.length > 0) {
          const sources = constants[row.index]?.workflows ?? []

          // Extracting the first item
          const firstItem = sources.slice(0, 1)

          // Checking if there are more items
          const moreItemsCount = sources.length - 1
          const hasMoreItems = moreItemsCount > 0

          // Rendering logic
          let renderedItems: string | ReactNode = firstItem
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ?.map((it: any) => {
              return `${it?.name} v${parseFloat(it?.version).toFixed(1)}`
            })
            .join(", ")
          if (hasMoreItems) {
            renderedItems = (
              <Typography variant="p" noWrap>
                {`${renderedItems}`}{" "}
                <span style={{ textDecoration: "underline" }}>
                  {t("projectConstants.tooltip.andMore", { count: moreItemsCount })}
                </span>
              </Typography>
            )
          }

          return (
            <Tooltip
              placement="top"
              title={
                <Grid item padding={1}>
                  {t("projectConstants.tooltip.title")}
                  <Grid item container marginTop={0.5}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {sources?.map((wf: any, idx: number) => {
                      return (
                        <Fragment key={`${row.original.name}-${wf?.name}-v${wf?.version}`}>
                          {idx !== sources?.length - 1 || sources?.length === 1 ? "" : "and "}
                          {wf?.name} {`v${parseFloat(wf?.version)?.toFixed(1)}`}
                          {idx !== sources?.length - 1 ? ", " : " "}
                        </Fragment>
                      )
                    })}
                  </Grid>
                </Grid>
              }
            >
              <Typography variant="p" noWrap>
                {sources.length > 0 ? renderedItems : "_"}
              </Typography>
            </Tooltip>
          )
        }
      },
    },
  ]

  return (
    <BaseTable
      data={constants}
      columns={columns as MRT_ColumnDef[]}
      disableToolbar
      title="Project Constants"
      pageSize={10}
      isLoading={!!isCurrentProjectFetching}
      enableOrdering={false}
    />
  )
}
